import { api } from '../api.svelte.js';
import { createNode, folder, file, root } from './seed.js';
import { filesystemState, resetFsMap, flattenTree } from './store.svelte.js';
import { touchFilesystem } from './mutations.js';

let cloudTree = [];

export async function loadCloudFilesystem() {
  try {
    const nodes = await api.fsList();
    cloudTree = nodes;
    const tree = buildTree(nodes);
    assignPaths(tree, '/');
    Object.assign(root, tree);
    resetFsMap(flattenTree(root));
    touchFilesystem();
    filesystemState.ready = true;
  } catch (err) {
    console.warn('[cloud-fs] failed to load:', err);
    filesystemState.ready = true;
  }
}

export async function createCloudNode(parentPath, name, kind, opts = {}) {
  const parent = findCloudNodeByPath(parentPath);
  const payload = { parent_id: parent?.id ?? null, name, kind };
  if (kind === 'file') {
    payload.r2_key = opts.r2Key;
    payload.size = opts.size ?? 0;
    payload.mime_type = opts.mimeType ?? '';
  }
  await api.fsCreate(payload);
  await loadCloudFilesystem();
}

export async function updateCloudNode(id, changes) {
  await api.fsUpdate(id, changes);
  await loadCloudFilesystem();
}

export async function deleteCloudNode(id) {
  await api.fsDelete(id);
  await loadCloudFilesystem();
}

export async function nukeCloudDrive() {
  await api.fsNuke();
  await loadCloudFilesystem();
}

export async function getCloudFileURL(id) {
  const res = await api.fsURL(id);
  return res;
}

export function isCloudPath(path) {
  return cloudTree.length > 0;
}

function findCloudNodeByPath(path) {
  const segments = path.split('/').filter(Boolean);
  return findChild(cloudTree, segments);
}

function findChild(nodes, segments, parentId = null) {
  if (segments.length === 0) return null;
  const [head, ...rest] = segments;
  const match = nodes.find(n => n.parent_id === parentId && n.name === head);
  if (!match) return null;
  if (rest.length === 0) return match;
  return findChild(nodes, rest, match.id);
}

function buildTree(nodes) {
  if (!nodes || nodes.length === 0) return {};
  const nodeMap = {};
  for (const n of nodes) {
    nodeMap[n.id] = {
      id: `cloud-${n.id}`,
      name: n.name,
      path: '',
      kind: n.kind,
      icon: n.kind === 'dir' ? 'folder' : 'document',
      children: [],
      size: n.size ? formatSize(n.size) : '--',
      mimeType: n.mime_type || null,
      cloudId: n.id,
      cloudR2Key: n.r2_key || null,
    };
  }
  for (const n of nodes) {
    const node = nodeMap[n.id];
    if (n.parent_id !== null && nodeMap[n.parent_id]) {
      nodeMap[n.parent_id].children.push(node);
    }
  }
  const roots = nodes.filter(n => n.parent_id === null).map(n => nodeMap[n.id]);
  return roots.length === 1 ? roots[0] : { id: 'cloud-root', name: 'Cloud Drive', path: '/', kind: 'folder', icon: 'drive-hd', children: roots, cloudId: null };
}

function assignPaths(node, basePath) {
  node.path = basePath === '/' ? `/${node.name}` : `${basePath}/${node.name}`;
  for (const child of node.children) {
    assignPaths(child, node.path);
  }
}

function formatSize(bytes) {
  if (bytes === 0) return 'Zero KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
